import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Calculate GST on an Invoice (With Examples)',
      'description': 'CGST, SGST, IGST — exactly how to calculate each one, worked examples at every GST slab, and the inclusive vs exclusive GST formula.',
      'url': 'https://www.invilic.com/blog/how-to-calculate-gst-invoice',
      'datePublished': '2025-05-20',
      'dateModified': '2025-05-20',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'How do I calculate 18% GST on an invoice amount?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'To calculate 18% GST on a net (exclusive) amount: multiply the amount by 0.18. Example: ₹10,000 × 0.18 = ₹1,800 GST. Total payable = ₹11,800. For intra-state: CGST = ₹900 (9%) + SGST = ₹900 (9%). For inter-state: IGST = ₹1,800 (18%).' },
        },
        {
          '@type': 'Question',
          'name': 'How do I calculate the GST-exclusive amount from a GST-inclusive price?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'To find the net amount from a GST-inclusive price: divide the total by (1 + GST rate). Example at 18%: ₹11,800 ÷ 1.18 = ₹10,000 net. The GST amount is ₹11,800 − ₹10,000 = ₹1,800. This is the "reverse GST" or "back-calculate GST" formula.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the difference between CGST, SGST, and IGST on an invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'CGST (Central GST) and SGST (State GST) are each levied at half the applicable rate on intra-state supplies (buyer and seller in the same state). For an 18% supply: 9% CGST + 9% SGST. IGST (Integrated GST) is levied at the full rate on inter-state supplies (buyer and seller in different states). For 18%: 18% IGST — no split.' },
        },
        {
          '@type': 'Question',
          'name': 'What if my invoice has line items at multiple GST rates?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Show each rate separately on the invoice. Calculate the GST amount for each line item or group of items at the same rate, then add up all the GST amounts to arrive at the total GST. For example, if you have items at 5% and 18%, show the 5% GST subtotal and the 18% GST subtotal before showing the grand total.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const gstSlabs = [
  { rate: '0%', examples: 'Milk, fresh vegetables, cereals, educational services', note: 'Exempt — no GST charged, no ITC claim' },
  { rate: '5%', examples: 'Household necessities, some textiles, economy class air travel', note: 'Low rate for essential goods and services' },
  { rate: '12%', examples: 'Business class travel, processed food, smartphones', note: 'Standard reduced rate' },
  { rate: '18%', examples: 'Most services, software, electronics, restaurants with AC', note: 'Most common rate for services' },
  { rate: '28%', examples: 'Luxury goods, tobacco, aerated drinks, automobiles', note: 'Highest rate, plus cess in some cases' },
];

const examples = [
  { rate: '5%', net: '20,000', cgst: '500 (2.5%)', sgst: '500 (2.5%)', igst: '1,000 (5%)', total: '21,000' },
  { rate: '12%', net: '15,000', cgst: '900 (6%)', sgst: '900 (6%)', igst: '1,800 (12%)', total: '16,800' },
  { rate: '18%', net: '10,000', cgst: '900 (9%)', sgst: '900 (9%)', igst: '1,800 (18%)', total: '11,800' },
  { rate: '28%', net: '5,000', cgst: '700 (14%)', sgst: '700 (14%)', igst: '1,400 (28%)', total: '6,400' },
];

export default function HowToCalculateGstPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Calculate GST on an Invoice (With Examples) | Invilic Blog"
        description="Step-by-step GST calculation with worked examples at all GST slabs — CGST, SGST, IGST, inclusive and exclusive formulas."
        canonical="https://www.invilic.com/blog/how-to-calculate-gst-invoice"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">India GST</span>
          </div>
          <span className="inline-block bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">India GST</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            How to Calculate GST on an Invoice (With Examples)
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">May 20, 2025 · 5 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            To calculate GST on an invoice, multiply the net (excluding GST) amount by the GST rate percentage. For intra-state supplies, split the total GST equally into CGST and SGST. For inter-state supplies, the full amount becomes IGST. Here are all the formulas with worked examples at every GST slab.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">The GST Rate Slabs in India</h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Rate</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Common Examples</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Note</th>
                </tr>
              </thead>
              <tbody>
                {gstSlabs.map(({ rate, examples: ex, note }) => (
                  <tr key={rate} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="px-4 py-3 font-bold text-green-700 dark:text-green-400 border border-gray-100 dark:border-gray-800">{rate}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{ex}</td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-500 text-xs border border-gray-100 dark:border-gray-800">{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">The GST Calculation Formula</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="p-5 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/30 rounded-2xl">
              <p className="font-bold text-green-700 dark:text-green-300 mb-2 text-sm">GST-Exclusive (Add GST to Net)</p>
              <p className="text-gray-700 dark:text-gray-300 text-sm font-mono">GST Amount = Net × Rate</p>
              <p className="text-gray-700 dark:text-gray-300 text-sm font-mono mt-1">Total = Net + GST Amount</p>
              <p className="text-gray-500 dark:text-gray-400 text-xs mt-3">Example: ₹10,000 × 18% = ₹1,800 GST → Total ₹11,800</p>
            </div>
            <div className="p-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 rounded-2xl">
              <p className="font-bold text-blue-700 dark:text-blue-300 mb-2 text-sm">GST-Inclusive (Back-Calculate from Total)</p>
              <p className="text-gray-700 dark:text-gray-300 text-sm font-mono">Net = Total ÷ (1 + Rate)</p>
              <p className="text-gray-700 dark:text-gray-300 text-sm font-mono mt-1">GST = Total − Net</p>
              <p className="text-gray-500 dark:text-gray-400 text-xs mt-3">Example: ₹11,800 ÷ 1.18 = ₹10,000 Net → GST ₹1,800</p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">CGST vs SGST vs IGST: The Split</h2>
          <p className="mb-4">
            The total GST rate is always the same, but how it is split depends on whether the supply is intra-state or inter-state:
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="p-5 border border-gray-100 dark:border-gray-800 rounded-2xl">
              <p className="font-bold text-gray-900 dark:text-white mb-2 text-sm">Intra-State Supply (same state)</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">CGST = Rate ÷ 2</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">SGST = Rate ÷ 2</p>
              <p className="text-xs text-gray-400 mt-2">At 18%: CGST 9% + SGST 9%</p>
            </div>
            <div className="p-5 border border-gray-100 dark:border-gray-800 rounded-2xl">
              <p className="font-bold text-gray-900 dark:text-white mb-2 text-sm">Inter-State Supply (different states)</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">IGST = Full rate</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">(No CGST or SGST split)</p>
              <p className="text-xs text-gray-400 mt-2">At 18%: IGST 18%</p>
            </div>
          </div>

          <BlogCTA
            text="Skip the manual calculations — Invilic auto-calculates CGST, SGST, and IGST based on your place of supply selection."
            to="/create/gst"
            btnText="Create GST Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Worked Examples at Each GST Slab (₹ amounts)</h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-3 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Rate</th>
                  <th className="text-left px-3 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Net (₹)</th>
                  <th className="text-left px-3 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">CGST + SGST (intra)</th>
                  <th className="text-left px-3 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">IGST (inter)</th>
                  <th className="text-left px-3 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                {examples.map(({ rate, net, cgst, sgst, igst, total }) => (
                  <tr key={rate} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="px-3 py-3 font-bold text-green-700 dark:text-green-400 border border-gray-100 dark:border-gray-800">{rate}</td>
                    <td className="px-3 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{net}</td>
                    <td className="px-3 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{cgst} + {sgst}</td>
                    <td className="px-3 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{igst}</td>
                    <td className="px-3 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">{total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Use Invilic to create a GST invoice — CGST, SGST, and IGST auto-calculated at every rate. Free, no account needed."
            to="/create/gst"
            btnText="Create GST Invoice"
          />

        </article>
        <RelatedArticles current="/blog/how-to-calculate-gst-invoice" />
      </main>
    </div>
  );
}
